import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { publicApi } from '../../services/publicApi';
import { API_BASE, CF_ANALYTICS_URL } from '../../services/config';

interface SeoEntry {
  path: string;
  locale: string;
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImageUrl?: string;
  twitterCard?: string;
  noindex: boolean;
  priority?: number | null;
  lastmod?: string | null;
  schemaJson?: unknown;
}

const emptyEntry: SeoEntry = {
  path: '',
  locale: 'et',
  title: '',
  description: '',
  canonicalUrl: '',
  ogTitle: '',
  ogDescription: '',
  ogImageUrl: '',
  twitterCard: 'summary_large_image',
  noindex: false,
  priority: null,
  lastmod: '',
  schemaJson: undefined,
};

const SUPPORTED_LOCALES = ['et', 'en', 'ru', 'ua'] as const;
type Locale = typeof SUPPORTED_LOCALES[number];

function isValidPath(p: string): boolean {
  return /^\/[a-z]{2}\/(|[^\s]+)$/.test(p);
}

const SeoTab: React.FC = () => {
  const [entries, setEntries] = useState<SeoEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SeoEntry>(emptyEntry);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [required, setRequired] = useState<Array<{ path: string; locale: string; exists: boolean; defaults: any }>>([]);
  const [preview, setPreview] = useState<any | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [bulkAiLoading, setBulkAiLoading] = useState<boolean>(false);
  const [aiSuggestions, setAiSuggestions] = useState<Array<{ path: string; locale: string; suggestion: any }>>([]);
  const [aiContext, setAiContext] = useState<string>('');
  const [aiStatus, setAiStatus] = useState<{ ok: boolean; message?: string } | null>(null);
  const [seoChecks, setSeoChecks] = useState<{
    robots?: { status: 'unknown' | 'ok' | 'fail'; error?: string };
    sitemap?: { status: 'unknown' | 'ok' | 'fail'; error?: string };
    preview?: { status: 'unknown' | 'ok' | 'fail'; error?: string };
  }>({ robots: { status: 'unknown' }, sitemap: { status: 'unknown' }, preview: { status: 'unknown' } });
  const [overrideBase, setOverrideBase] = useState<string>(() => (typeof window !== 'undefined' ? window.location.origin : ''));

  const createMissingAll = async () => {
    try {
      await api.post('/api/admin/seo/ensure-all');
      await load();
    } catch (e) {
      setError('Failed to create missing entries');
    }
  };

  const createFromDefault = async (r: { path: string; locale: string; defaults: any }) => {
    try {
      const payload = {
        path: r.path,
        locale: r.locale,
        ...r.defaults,
        noindex: !!r.defaults?.noindex,
      };
      await api.post('/api/admin/seo/upsert', payload);
      await load();
    } catch (e) {
      setError('Failed to create entry');
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const resp = await api.get('/api/admin/seo');
      setEntries(resp.data as SeoEntry[]);
      try {
        const req = await api.get('/api/admin/seo/required');
        setRequired((req.data?.entries || []) as any);
      } catch {}
      setError(null);
    } catch (e) {
      setError('Failed to load SEO entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onEdit = (entry: SeoEntry) => {
    setEditing({ ...entry });
    setJsonError(null);
    void fetchPreview(entry.path, entry.locale);
  };

  const onDelete = async (entry: SeoEntry) => {
    try {
      await api.delete('/api/admin/seo', { data: { path: entry.path, locale: entry.locale } });
      await load();
    } catch (e) {
      alert('Delete failed');
    }
  };

  const parseSchema = (val: string): unknown | undefined => {
    const trimmed = val.trim();
    if (!trimmed) return undefined;
    try {
      return JSON.parse(trimmed);
    } catch (e) {
      setJsonError('schemaJson must be valid JSON');
      return undefined;
    }
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setJsonError(null);
    if (!isValidPath(editing.path)) {
      setError('Path must start with /:locale/');
      return;
    }
    if (!SUPPORTED_LOCALES.includes(editing.locale as Locale)) {
      setError('Unsupported locale');
      return;
    }
    const schemaVal = parseSchema((editing as any).schemaJsonText || '');
    if ((editing as any).schemaJsonText && !schemaVal) {
      return; // json error already set
    }
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const canonicalCandidate = editing.canonicalUrl?.trim();
      const canonical = canonicalCandidate && /^https?:\/\//i.test(canonicalCandidate)
        ? canonicalCandidate
        : (origin && isValidPath(editing.path) ? `${origin}${editing.path}` : undefined);
      const payload = {
        path: editing.path,
        locale: editing.locale,
        title: editing.title || undefined,
        description: editing.description || undefined,
        canonicalUrl: canonical,
        ogTitle: editing.ogTitle || undefined,
        ogDescription: editing.ogDescription || undefined,
        ogImageUrl: editing.ogImageUrl || undefined,
        twitterCard: editing.twitterCard || undefined,
        noindex: !!editing.noindex,
        priority: editing.priority === null || editing.priority === undefined || editing.priority === ('' as unknown as number) ? undefined : Number(editing.priority),
        lastmod: editing.lastmod || undefined,
        schemaJson: schemaVal,
      };
      await api.post('/api/admin/seo/upsert', payload);
      await load();
      setEditing(emptyEntry);
      setError(null);
    } catch (e) {
      setError('Save failed');
    }
  };

  const inferPageType = (path: string): 'home' | 'privacy' | 'terms' | 'custom' => {
    if (/^\/[a-z]{2}\/($|\?$|#)/.test(path)) return 'home';
    if (/^\/[a-z]{2}\/privacy(\/|$|\?|#)/.test(path)) return 'privacy';
    if (/^\/[a-z]{2}\/terms(\/|$|\?|#)/.test(path)) return 'terms';
    return 'custom';
  };

  const generateWithAI = async () => {
    if (!isValidPath(editing.path) || !SUPPORTED_LOCALES.includes(editing.locale as Locale)) {
      setError('Set a valid path and locale first');
      return;
    }
    setAiLoading(true);
    try {
      let context = aiContext;
      if (!context) {
        try {
          context = await buildAutoContext();
          setAiContext(context);
        } catch {
          // ignore auto-context failure; proceed without it
        }
      }
      const res = await api.post('/api/admin/seo/autogen', {
        path: editing.path,
        locale: editing.locale,
        pageType: inferPageType(editing.path),
        sourceText: context || undefined,
      });
      const s = res.data || {};
      setEditing((prev) => ({
        ...prev,
        title: s.title ?? prev.title,
        description: s.description ?? prev.description,
        ogTitle: s.ogTitle ?? s.title ?? prev.ogTitle,
        ogDescription: s.ogDescription ?? s.description ?? prev.ogDescription,
        canonicalUrl: s.canonicalUrl ?? prev.canonicalUrl,
        // expose schema JSON in the textarea for review before save
        ...(s.schemaJson ? ({ schemaJson: s.schemaJson, schemaJsonText: JSON.stringify(s.schemaJson, null, 2) } as any) : {}),
      }));
    } catch (e) {
      setError('AI generation failed');
    } finally {
      setAiLoading(false);
    }
  };

  const buildAutoContext = async (): Promise<string> => {
    try {
      // Use Estonia for snapshot; concise summary focusing on risk and top categories
      const data = await publicApi.getDashboardData(undefined, undefined, undefined, 'EST');
      const overall = data.overallRisk || { score: 0, change24h: 0 } as any;
      const topCats = (data.categoryBreakdown || [])
        .slice(0, 3)
        .map((c) => `${c.name}:${c.score.toFixed(1)}`)
        .join(', ');
      const articleTitles = (data.criticalArticles || [])
        .slice(0, 2)
        .map((a) => a.title)
        .join(' | ');
      const summary = [
        `Risk snapshot (EST): overall=${overall.score == null ? 'no live data' : overall.score.toFixed(1)}, change24h=${overall.change24h}`,
        topCats ? `Top categories: ${topCats}` : '',
        data.stats ? `ArticlesToday=${data.stats.articlesAnalyzedToday}, Sources=${data.stats.sourcesMonitored}` : '',
        articleTitles ? `Critical titles: ${articleTitles}` : '',
      ]
        .filter(Boolean)
        .join('\n');
      return summary;
    } catch (e) {
      return '';
    }
  };

  const checkOpenRouter = async () => {
    try {
      setAiStatus(null);
      const res = await api.get('/api/admin/seo/autogen/status');
      const ok = !!res.data?.ok;
      const model = res.data?.model as string | undefined;
      const reason = res.data?.error as string | undefined;
      const msg = ok
        ? `OpenRouter OK${model ? ` (${model})` : ''}`
        : `OpenRouter check failed${reason ? `: ${reason}` : ''}`;
      setAiStatus({ ok, message: msg });
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed';
      setAiStatus({ ok: false, message: String(msg) });
    }
  };

  const generateMissingAI = async (onlyLocale?: string) => {
    setBulkAiLoading(true);
    try {
      const payload = onlyLocale ? { locale: onlyLocale, sourceText: aiContext || undefined } : { sourceText: aiContext || undefined };
      const res = await api.post('/api/admin/seo/autogen/bulk', payload);
      const suggestions: Array<{ path: string; locale: string; suggestion: any }> = res.data?.suggestions || [];
      setAiSuggestions(suggestions);
      if (suggestions.length > 0) {
        const first = suggestions[0];
        setEditing({
          ...emptyEntry,
          path: first.path,
          locale: first.locale as Locale,
          title: first.suggestion?.title || '',
          description: first.suggestion?.description || '',
          canonicalUrl: first.suggestion?.canonicalUrl || '',
          ogTitle: first.suggestion?.ogTitle || first.suggestion?.title || '',
          ogDescription: first.suggestion?.ogDescription || first.suggestion?.description || '',
          ...(first.suggestion?.schemaJson ? ({ schemaJson: first.suggestion.schemaJson, schemaJsonText: JSON.stringify(first.suggestion.schemaJson, null, 2) } as any) : {}),
        } as any);
        setError(null);
      } else {
        setError('No missing required entries or AI returned no suggestions');
      }
    } catch (e) {
      setError('Bulk AI generation failed');
    } finally {
      setBulkAiLoading(false);
    }
  };

  const runSeoChecks = async () => {
    const base = API_BASE || '';
    const robotsUrl = `${base}/robots.txt`;
    const sitemapUrl = `${base}/sitemap.xml`;
    const prevUrl = `${base}/api/public/seo?path=${encodeURIComponent(editing.path || '/et/')}&locale=${encodeURIComponent(editing.locale || 'et')}`;
    setSeoChecks({ robots: { status: 'unknown' }, sitemap: { status: 'unknown' }, preview: { status: 'unknown' } });
    try {
      const r = await fetch(robotsUrl, { method: 'GET' });
      setSeoChecks((s) => ({ ...s, robots: { status: r.ok ? 'ok' : 'fail', error: r.ok ? undefined : `${r.status}` } }));
    } catch (e: any) {
      setSeoChecks((s) => ({ ...s, robots: { status: 'fail', error: e?.message || 'error' } }));
    }
    try {
      const r = await fetch(sitemapUrl, { method: 'GET' });
      setSeoChecks((s) => ({ ...s, sitemap: { status: r.ok ? 'ok' : 'fail', error: r.ok ? undefined : `${r.status}` } }));
    } catch (e: any) {
      setSeoChecks((s) => ({ ...s, sitemap: { status: 'fail', error: e?.message || 'error' } }));
    }
    try {
      const r = await fetch(prevUrl, { method: 'GET', headers: { Accept: 'application/json' } });
      setSeoChecks((s) => ({ ...s, preview: { status: r.ok ? 'ok' : 'fail', error: r.ok ? undefined : `${r.status}` } }));
    } catch (e: any) {
      setSeoChecks((s) => ({ ...s, preview: { status: 'fail', error: e?.message || 'error' } }));
    }
  };

  const applySuggestion = async (s: { path: string; locale: string; suggestion: any }, reload: boolean = true) => {
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      // Build a safe absolute canonical URL using URL() API
      let canonical: string | undefined;
      try {
        if (s.suggestion?.canonicalUrl && /^https?:\/\//i.test(s.suggestion.canonicalUrl)) {
          canonical = new URL(s.suggestion.canonicalUrl).toString();
        } else if (origin) {
          const base = origin.endsWith('/') ? origin : `${origin}/`;
          const path = s.path.startsWith('/') ? s.path.substring(1) : s.path;
          canonical = new URL(path || '', base).toString();
        }
      } catch {
        // Leave canonical undefined if construction fails; backend treats it as optional
        canonical = undefined;
      }

      const payload: any = {
        path: s.path,
        locale: s.locale,
        title: s.suggestion?.title || undefined,
        description: s.suggestion?.description || undefined,
        ogTitle: s.suggestion?.ogTitle || s.suggestion?.title || undefined,
        ogDescription: s.suggestion?.ogDescription || s.suggestion?.description || undefined,
        ogImageUrl: undefined,
        twitterCard: 'summary_large_image',
        noindex: false,
        priority: /^\/[a-z]{2}\/($|\?|#)/.test(s.path) ? 0.8 : 0.4,
        schemaJson: s.suggestion?.schemaJson || undefined,
      };
      if (canonical) payload.canonicalUrl = canonical;
      await api.post('/api/admin/seo/upsert', payload);
      if (reload) {
        await load();
      }
      setError(null);
    } catch (e) {
      setError('Apply suggestion failed');
    }
  };

  const applyAllSuggestions = async () => {
    if (!aiSuggestions.length) return;
    try {
      // Apply sequentially; reload once at the end to avoid repeated GETs
      for (const s of aiSuggestions) {
        await applySuggestion(s, false);
      }
      await load();
      setAiSuggestions([]);
    } catch (e) {
      setError('Apply all failed');
    }
  };

  const fetchPreview = async (path: string, locale: string) => {
    try {
      setPreviewLoading(true);
      const data = await publicApi.getSeoMeta(path, locale);
      setPreview(data);
    } catch (e) {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => (a.locale + a.path).localeCompare(b.locale + b.path));
  }, [entries]);

  return (
    <div className="tc-card p-6">
      <h3 className="text-xl font-semibold mb-4 tc-text-primary">SEO Management</h3>

      {loading ? <div>Loading…</div> : null}
      {error ? <div className="error-bg p-2 rounded mb-3 text-sm">{error}</div> : null}
      {jsonError ? <div className="error-bg p-2 rounded mb-3 text-sm">{jsonError}</div> : null}

      {/* AI-first controls (no manual editing) */}
      <div className="tc-card p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">Path</label>
            <input className="tc-input w-full" value={editing.path} onChange={(e) => setEditing({ ...editing, path: e.target.value })} placeholder="/et/ or /en/terms" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Locale</label>
            <select className="tc-input w-full" value={editing.locale} onChange={(e) => setEditing({ ...editing, locale: e.target.value })}>
              {SUPPORTED_LOCALES.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button type="button" className="tc-btn tc-btn-secondary" onClick={generateWithAI} disabled={aiLoading}>
              {aiLoading ? 'Generating…' : 'Generate with AI'}
            </button>
            <button type="button" className="tc-btn tc-btn-secondary" onClick={() => fetchPreview(editing.path || '/et/', editing.locale || 'et')}>Refresh Preview</button>
          </div>
        </div>
        <div className="mt-2 text-xs text-gray-500">Manual fields removed. Use AI to generate and bulk tools below to override canonicals.</div>
      </div>

      {/* Preview & SEO */}
      <div className="tc-card p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold tc-text-primary">Preview & SEO</h4>
          <div className="flex gap-2">
            <button type="button" className="tc-btn tc-btn-secondary" onClick={() => fetchPreview(editing.path || '/et/', editing.locale || 'et')}>Refresh Preview</button>
            <a className="tc-btn tc-btn-secondary" href={`${API_BASE || ''}/sitemap.xml`} target="_blank" rel="noreferrer">Open sitemap.xml</a>
            <a className="tc-btn tc-btn-secondary" href={`${API_BASE || ''}/robots.txt`} target="_blank" rel="noreferrer">Open robots.txt</a>
            <a className="tc-btn tc-btn-secondary" href={CF_ANALYTICS_URL} target="_blank" rel="noreferrer">Cloudflare Analytics</a>
            <button type="button" className="tc-btn tc-btn-secondary" onClick={() => runSeoChecks()}>Run Checks</button>
          </div>
        </div>
        <div className="mb-3 text-sm">
          <div className="flex gap-4 flex-wrap">
            <div>robots.txt: {seoChecks.robots?.status === 'ok' ? '✅ OK' : seoChecks.robots?.status === 'fail' ? `❌ ${seoChecks.robots?.error || ''}` : '—'}</div>
            <div>sitemap.xml: {seoChecks.sitemap?.status === 'ok' ? '✅ OK' : seoChecks.sitemap?.status === 'fail' ? `❌ ${seoChecks.sitemap?.error || ''}` : '—'}</div>
            <div>SEO JSON: {seoChecks.preview?.status === 'ok' ? '✅ OK' : seoChecks.preview?.status === 'fail' ? `❌ ${seoChecks.preview?.error || ''}` : '—'}</div>
          </div>
        </div>
        <div className="mb-2 flex items-center gap-2 flex-wrap">
          <input className="tc-input" style={{ minWidth: 320 }} placeholder="https://www.example.com" value={overrideBase} onChange={(e) => setOverrideBase(e.target.value)} />
          <button type="button" className="tc-btn tc-btn-primary" onClick={async () => {
            if (!window.confirm('Reset SEO to base (set base, delete all, recreate required, override canonicals, clear caches)?')) return;
            await api.post('/api/admin/seo/reset', { base: overrideBase });
            await load();
          }}>Reset SEO to Base</button>
          <button type="button" className="tc-btn tc-btn-secondary" onClick={async () => {
            if (!window.confirm('Force all canonical URLs to <base> + path? This overwrites existing values.')) return;
            await api.post('/api/admin/seo/canonicals/override', { base: overrideBase });
            await load();
          }}>Override Canonicals to Base + Path</button>
          <button type="button" className="tc-btn tc-btn-secondary" onClick={async () => {
            if (!window.confirm('Set all canonicals to SITE_URL + path?')) return;
            await api.post('/api/admin/seo/canonicals/normalize-all');
            await load();
          }}>Use SITE_URL for All</button>
          <button type="button" className="tc-btn tc-btn-secondary" onClick={async () => {
            if (!window.confirm('Clear canonicalUrl for all entries? The server will compute canonicals at render time.')) return;
            await api.post('/api/admin/seo/canonicals/clear-all');
            await load();
          }}>Clear All Canonicals</button>
          <button type="button" className="tc-btn tc-btn-secondary" onClick={async () => {
            if (!window.confirm('Delete ALL SEO entries? This cannot be undone.')) return;
            await api.post('/api/admin/seo/delete-all');
            await load();
          }}>Delete ALL SEO Entries</button>
          <button type="button" className="tc-btn tc-btn-secondary" onClick={async () => {
            if (!window.confirm('Delete all and recreate required entries using the base URL?')) return;
            await api.post('/api/admin/seo/recreate-required', { base: overrideBase });
            await load();
          }}>Recreate Required (Use Base)</button>
        </div>
        {previewLoading ? (
          <div>Loading preview…</div>
        ) : preview ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm"><strong>Title:</strong> {preview.title || '—'}</div>
              <div className="text-sm"><strong>Description:</strong> {preview.description || '—'}</div>
              <div className="text-sm"><strong>Canonical:</strong> {preview.canonicalUrl || '—'}</div>
              <div className="text-sm"><strong>Robots:</strong> {preview.noindex ? 'noindex' : 'index'}</div>
              <div className="text-sm"><strong>OG Title:</strong> {preview.ogTitle || '—'}</div>
              <div className="text-sm"><strong>OG Description:</strong> {preview.ogDescription || '—'}</div>
              <div className="text-sm"><strong>OG Image:</strong> {preview.ogImageUrl || '—'}</div>
              <div className="text-sm"><strong>Twitter Card:</strong> {preview.twitterCard || '—'}</div>
            </div>
            <div>
              {/* Simple social card preview */}
              <div className="border rounded-lg overflow-hidden max-w-md bg-white">
                {preview.ogImageUrl ? (
                  <img src={preview.ogImageUrl} alt="OG" className="w-full h-40 object-cover" />
                ) : (
                  <div className="w-full h-40 bg-gray-200 flex items-center justify-center text-gray-500">No image</div>
                )}
                <div className="p-3">
                  <div className="text-xs text-gray-500 mb-1">{preview.canonicalUrl || 'https://example.com'}</div>
                  <div className="text-sm font-semibold mb-1">{preview.ogTitle || preview.title || 'Title'}</div>
                  <div className="text-xs text-gray-700 line-clamp-2">{preview.ogDescription || preview.description || 'Description'}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500">No preview yet. Click “Refresh Preview”. Note: preview shows stored values. Save first to preview new changes.</div>
        )}
      </div>

      {/* Required entries (AI-driven) */}
      <div className="mb-4 flex items-center justify-between">
        <h4 className="text-lg font-semibold tc-text-primary">Required Pages</h4>
        <div className="flex gap-2">
          <button type="button" className="tc-btn tc-btn-secondary" onClick={() => generateMissingAI()} disabled={bulkAiLoading}>
            {bulkAiLoading ? 'Generating…' : 'Generate missing (AI)'}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto mb-8">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Locale</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {required.map((r) => (
              <tr key={`${r.locale}:${r.path}`}>
                <td className="px-3 py-2 text-sm text-gray-700">{r.locale}</td>
                <td className="px-3 py-2 text-sm text-gray-700">{r.path}</td>
                <td className="px-3 py-2 text-sm text-gray-700">{r.exists ? 'OK' : 'Missing'}</td>
                <td className="px-3 py-2 text-right space-x-2">
                  <button className="tc-btn tc-btn-secondary" onClick={() => fetchPreview(r.path, r.locale)}>Preview</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AI Suggestions */}
      {aiSuggestions.length > 0 && (
        <div className="tc-card p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-lg font-semibold tc-text-primary">AI Suggestions</h4>
            <div className="flex gap-2">
              <button className="tc-btn tc-btn-secondary" onClick={applyAllSuggestions}>Apply All</button>
              <button className="tc-btn tc-btn-secondary" onClick={() => setAiSuggestions([])}>Clear</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Locale</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {aiSuggestions.map((s) => (
                  <tr key={`${s.locale}:${s.path}`}>
                    <td className="px-3 py-2 text-sm text-gray-700">{s.locale}</td>
                    <td className="px-3 py-2 text-sm text-gray-700">{s.path}</td>
                    <td className="px-3 py-2 text-sm text-gray-700">{s.suggestion?.title || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 truncate max-w-xs" title={s.suggestion?.description || ''}>{s.suggestion?.description || '—'}</td>
                    <td className="px-3 py-2 text-right space-x-2">
                      <button className="tc-btn tc-btn-secondary" onClick={() => applySuggestion(s)}>Apply</button>
                      <button
                        className="tc-btn tc-btn-secondary"
                        onClick={() => setEditing({
                          ...emptyEntry,
                          path: s.path,
                          locale: s.locale as Locale,
                          title: s.suggestion?.title || '',
                          description: s.suggestion?.description || '',
                          canonicalUrl: s.suggestion?.canonicalUrl || '',
                          ogTitle: s.suggestion?.ogTitle || s.suggestion?.title || '',
                          ogDescription: s.suggestion?.ogDescription || s.suggestion?.description || '',
                          ...(s.suggestion?.schemaJson ? ({ schemaJson: s.suggestion.schemaJson, schemaJsonText: JSON.stringify(s.suggestion.schemaJson, null, 2) } as any) : {}),
                        } as any)}
                      >Prefill</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* List */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Locale</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Noindex</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedEntries.map((e) => (
              <tr key={`${e.locale}:${e.path}`}>
                <td className="px-3 py-2 text-sm text-gray-700">{e.locale}</td>
                <td className="px-3 py-2 text-sm text-gray-700">{e.path}</td>
                <td className="px-3 py-2 text-sm text-gray-700">{e.title || '—'}</td>
                <td className="px-3 py-2 text-sm text-gray-700">{e.noindex ? 'yes' : 'no'}</td>
                <td className="px-3 py-2 text-right">
                  <button className="tc-btn tc-btn-secondary mr-2" onClick={() => onEdit(e)}>Edit</button>
                  <button className="tc-btn tc-btn-secondary" onClick={() => onDelete(e)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SeoTab;
 
